/**
 * Marca de agua con la bandera del país de destino, de fondo en el popup de escaneo
 * (pedido del usuario 2026-09-10: "muy leve, casi fumé, que se lea todo adelante").
 *
 * SVG inline (sin emoji: en Windows las banderas se ven como letras). Opacidad 0.07 en claro
 * y 0.10 en oscuro. Se coloca ABSOLUTA dentro de un contenedor `relative isolate` y con
 * `-z-10`, así queda encima del fondo del popup y debajo de todo el contenido.
 */
type Site = 'MLA' | 'MLM' | 'MLB' | 'MLC' | 'MCO' | 'MPE' | 'MLU' | 'MEC';

const SITE_TO_COUNTRY: Record<string, string> = {
  MLA: 'AR',
  MLM: 'MX',
  MLB: 'BR',
  MLC: 'CL',
  MCO: 'CO',
  MPE: 'PE',
  MLU: 'UY',
  MEC: 'EC',
};

export function siteToCountry(site: string | null | undefined): string | null {
  if (!site) return null;
  return SITE_TO_COUNTRY[site] ?? null;
}

function FlagSvg({ country }: { country: string }) {
  // viewBox 900×600 (3:2). Formas simplificadas, suficientes como marca de agua.
  switch (country) {
    case 'AR':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="900" height="600" fill="#74ACDF" />
          <rect y="200" width="900" height="200" fill="#ffffff" />
          <circle cx="450" cy="300" r="62" fill="#F6B40E" />
        </svg>
      );
    case 'MX':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="300" height="600" fill="#006847" />
          <rect x="300" width="300" height="600" fill="#ffffff" />
          <rect x="600" width="300" height="600" fill="#CE1126" />
          <circle cx="450" cy="300" r="70" fill="#8C6239" />
        </svg>
      );
    case 'BR':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="900" height="600" fill="#009C3B" />
          <polygon points="450,80 820,300 450,520 80,300" fill="#FFDF00" />
          <circle cx="450" cy="300" r="150" fill="#002776" />
          <path d="M310 270 Q450 240 590 320" stroke="#ffffff" strokeWidth="22" fill="none" />
        </svg>
      );
    case 'CL':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="900" height="300" fill="#ffffff" />
          <rect y="300" width="900" height="300" fill="#D52B1E" />
          <rect width="300" height="300" fill="#0039A6" />
          <polygon
            points="150,80 172,148 244,148 186,190 208,258 150,216 92,258 114,190 56,148 128,148"
            fill="#ffffff"
          />
        </svg>
      );
    case 'CO':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="900" height="300" fill="#FCD116" />
          <rect y="300" width="900" height="150" fill="#003893" />
          <rect y="450" width="900" height="150" fill="#CE1126" />
        </svg>
      );
    case 'PE':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="300" height="600" fill="#D91023" />
          <rect x="300" width="300" height="600" fill="#ffffff" />
          <rect x="600" width="300" height="600" fill="#D91023" />
        </svg>
      );
    case 'UY':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="900" height="600" fill="#ffffff" />
          {[1, 3, 5, 7].map((i) => (
            <rect key={i} y={i * 66.7} width="900" height="66.7" fill="#0038A8" />
          ))}
          <rect width="300" height="333" fill="#ffffff" />
          <circle cx="150" cy="166" r="60" fill="#FCD116" />
        </svg>
      );
    case 'EC':
      return (
        <svg viewBox="0 0 900 600" xmlns="http://www.w3.org/2000/svg" className="h-auto w-full">
          <rect width="900" height="300" fill="#FFDD00" />
          <rect y="300" width="900" height="150" fill="#034EA2" />
          <rect y="450" width="900" height="150" fill="#ED1C24" />
        </svg>
      );
    default:
      return null;
  }
}

export function CountryFlagWatermark({
  site,
  enabled = true,
}: {
  site: string | null | undefined;
  enabled?: boolean;
}) {
  const country = siteToCountry(site);
  if (!enabled || !country) return null;
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-[0.07] dark:opacity-[0.10]"
    >
      <div className="w-[min(80%,640px)]">
        <FlagSvg country={country} />
      </div>
    </div>
  );
}

export type { Site };
